import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'antd';

// The most an empty state says. Past two sentences it stops being a way in
// and becomes a page to read, which is what the guide behind Learn more is
// for (KPE-009: "an icon, a one-line title, at most two sentences").
const MAX_SENTENCES = 2;

function ActionButton({ action, primary }) {
	const navigate = useNavigate();
	if (!action || !action.label) return null;
	return (
		<Button
			type={primary ? 'primary' : 'default'}
			icon={action.icon || null}
			className="empty-state__action"
			onClick={() => {
				if (typeof action.onClick === 'function') action.onClick();
				else if (action.to) navigate(action.to);
			}}
		>
			{action.label}
		</Button>
	);
}

/**
 * THE empty state: what a space is for and the one thing to do next, shown
 * only where the thing is truly empty — never for a filter or a search that
 * matched nothing, which keeps its own "No items match" line (KPE-009).
 *
 *   <EmptyState
 *     icon={<CARD_ICON />}
 *     title="Start with a card"
 *     description={['A card describes one kind of record…', 'Create one, or…']}
 *     primaryAction={{ label: 'Create a card', to: '/collections/…/cards/new' }}
 *     secondaryAction={{ label: 'Browse Discover', to: '/discover' }}
 *     learnMore="cards-and-elements/create-a-card"
 *   />
 *
 * `description` is a sentence or an array of them; only the first two are
 * drawn. Actions are `{ label, to }` or `{ label, onClick }` (an optional
 * `icon`): the primary is filled, the secondary default — never two filled.
 * `secondaryAction` may be a list of two, the most a header carries (§3).
 * `note` is for someone who cannot act here: it replaces BOTH actions, so a
 * reader gets who to ask rather than buttons the server would refuse.
 * `learnMore` is a guide id (`<section>/<slug>`) or `{ id, label }`.
 *
 * Not antd's `Empty`: that one draws an illustration, and the standards say
 * no illustrations (platform-standards § Lists, "Zero is not information").
 */
export default function EmptyState({
	icon = null,
	title,
	description = null,
	primaryAction = null,
	secondaryAction = null,
	note = null,
	learnMore = null,
	className = '',
	...rest
}) {
	const sentences = (Array.isArray(description) ? description : [description])
		.filter(Boolean)
		.slice(0, MAX_SENTENCES);
	const guide = typeof learnMore === 'string' ? { id: learnMore } : learnMore;
	const secondaries = (Array.isArray(secondaryAction) ? secondaryAction : [secondaryAction]).filter(action => action && action.label);
	const hasActions = !note && ((primaryAction && primaryAction.label) || secondaries.length > 0);

	return (
		<div className={`empty-state${className ? ` ${className}` : ''}`} {...rest}>
			{icon ? <span className="empty-state__icon" aria-hidden="true">{icon}</span> : null}
			{title ? <h2 className="empty-state__title">{title}</h2> : null}
			{sentences.length ? (
				<p className="empty-state__text">{sentences.join(' ')}</p>
			) : null}
			{note ? <p className="empty-state__note">{note}</p> : null}
			{hasActions ? (
				<div className="empty-state__actions">
					<ActionButton action={primaryAction} primary />
					{secondaries.map(action => <ActionButton key={action.label} action={action} />)}
				</div>
			) : null}
			{guide && guide.id ? (
				<Link className="empty-state__learn" to={`/docs/guides/${guide.id}`}>
					{guide.label || 'Learn more'}
				</Link>
			) : null}
		</div>
	);
}
